import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaArchive,
  FaFileAlt,
  FaFolderOpen,
  FaPaperclip,
  FaRegClock,
} from 'react-icons/fa';
import { FiCheckSquare, FiXCircle } from 'react-icons/fi';
import Swal from 'sweetalert2';

import RespostaSimNao from '../../../../Componentes/chkQuestao/CardQuestao/tipos/respostaSimNao';
import RespostaConformeNaoConforme from '../../../../Componentes/chkQuestao/CardQuestao/tipos/respostaConformeNaoConforme';
import RespostaTexto from '../../../../Componentes/chkQuestao/CardQuestao/tipos/respostaTexto';
import RespostaMultiplaEscolha from '../../../../Componentes/chkQuestao/CardQuestao/tipos/respostaMultiplaEscolha';
import RespostaUnicaEscolha from '../../../../Componentes/chkQuestao/CardQuestao/tipos/respostaUnicaEscolha';
import * as chkAuditoriaUtils from '../../../../Services/Auditoria/chkAuditoriaUtils';
import {
  buscarDiretorioAnexosQuestao,
  listarArquivosQuestaoResposta,
  uploadArquivosQuestaoResposta,
  vincularArquivosQuestaoResposta,
} from '../../../../Services/Auditoria/chkQuestaoRespostaArquivo';
import api from '../../../../Services/api';
import { useOperador } from '../../../../Hooks/useOperador';

import '../../../../Componentes/chkQuestao/CardQuestao/cardQuestao.css';
import '../index.css';

const getRespostaId = (resposta) =>
  resposta?.IDQuestao_Resposta ||
  resposta?.IDQuestaoResposta ||
  resposta?.IDResposta ||
  resposta?.id ||
  null;

const getOpcaoId = (opcao) =>
  opcao?.IDQuestao_Opcao ||
  opcao?.IDQuestaoOpcao ||
  opcao?.IDOpcao ||
  opcao?.id ||
  null;

const getOpcaoDescricao = (opcao) =>
  opcao?.descricaoOpcao ||
  opcao?.descricao ||
  opcao?.opcao ||
  opcao?.titulo ||
  opcao?.texto ||
  opcao?.label ||
  opcao?.resposta ||
  '';

const isOpcaoMarcada = (opcao) =>
  Boolean(
    opcao?.opcaoMarcada ??
    opcao?.marcada ??
    opcao?.selecionada ??
    opcao?.respostaMarcada ??
    false,
  );

const ACCEPTED_EXTENSIONS = new Set([
  '.pdf',
  '.jpeg',
  '.jpg',
  '.png',
  '.doc',
  '.docx',
  '.odt',
  '.xls',
  '.xlsx',
  '.ods',
]);

const ACCEPTED_MIME_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
]);

const MAX_FILES_PER_UPLOAD = 50;
const MAX_FILE_SIZE = 800 * 1024 * 1024;
const QUESTAO_ID_BADGE_VARIANT_CLASS = 'accQ-title-id--option2';

const QuestaoResponderCard = ({
  questao,
  onAbrirArquivo,
  onExibirArquivos,
  onAnexosChange,
  anexosVersion = 0,
  onStatusChange,
  readOnly = false,
  isSelected = false,
  onSavingChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [respostas, setRespostas] = useState([]);
  const [objeto, setObjeto] = useState(null);
  const [texto, setTexto] = useState('');
  const [draftValor, setDraftValor] = useState(null);
  const [draftOpcoesMap, setDraftOpcoesMap] = useState(null);
  const [draftDirty, setDraftDirty] = useState(false);
  const [descricaoExpandida, setDescricaoExpandida] = useState(false);
  const [mostrarToggleDescricao, setMostrarToggleDescricao] = useState(false);
  const [anexos, setAnexos] = useState([]);
  const [loadingAnexos, setLoadingAnexos] = useState(false);
  const [uploadingAnexos, setUploadingAnexos] = useState(false);
  const [arquivosSelecionados, setArquivosSelecionados] = useState([]);
  const descricaoRef = useRef(null);
  const fileInputRef = useRef(null);
  const descricaoMinimaParaToggle = 120;
  const { IDOperador, permissaoPrincipal } = useOperador();
  const selecionadaRef = useRef(false);

  useEffect(() => {
    if (!questao?.IDQuestao_Secao) return;
    onSavingChange?.(questao.IDQuestao_Secao, saving);
    return () => {
      onSavingChange?.(questao.IDQuestao_Secao, false);
    };
  }, [questao?.IDQuestao_Secao, saving, onSavingChange]);

  const carregarRespostas = useCallback(async ({ silent = false } = {}) => {
    if (!questao?.IDQuestao_Secao) return;
    try {
      if (!silent) setLoading(true);
      const respostasData = await chkAuditoriaUtils.getRespostasQuestao(
        questao.IDQuestao_Secao,
      );
      setRespostas(respostasData || []);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [questao]);

  const carregarObjeto = useCallback(async () => {
    if (!questao?.IDObjeto) {
      setObjeto(null);
      return;
    }
    const objetoData = await chkAuditoriaUtils.getObjetoQuestao(
      questao.IDObjeto,
    );
    console.log('Objeto', objetoData);
    setObjeto(objetoData);
  }, [questao]);

  const carregarAnexos = useCallback(async ({ silent = false } = {}) => {
    if (!questao?.IDQuestao_Secao) {
      setAnexos([]);
      return;
    }

    try {
      if (!silent) setLoadingAnexos(true);
      const anexosData = await listarArquivosQuestaoResposta(
        questao.IDQuestao_Secao,
      );
      setAnexos(anexosData || []);
    } finally {
      if (!silent) setLoadingAnexos(false);
    }
  }, [questao?.IDQuestao_Secao]);

  useEffect(() => {
    if (!questao) return;
    carregarRespostas();
    carregarObjeto();
    carregarAnexos();
  }, [questao, carregarRespostas, carregarObjeto, carregarAnexos]);

  useEffect(() => {
    if (!questao?.IDQuestao_Secao) return;
    carregarAnexos({ silent: true });
  }, [anexosVersion, questao?.IDQuestao_Secao, carregarAnexos]);

  useEffect(() => {
    if (!questao?.IDQuestao_Secao) return;
    onAnexosChange?.(questao, anexos);
  }, [anexos, questao, onAnexosChange]);

  useEffect(() => {
    setArquivosSelecionados([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [questao?.IDQuestao_Secao]);

  useEffect(() => {
    if (questao?.tipo !== 3) return;
    const respostaAtual = respostas?.[0];
    const valor =
      respostaAtual?.resposta ||
      respostaAtual?.descricao ||
      respostaAtual?.texto ||
      '';
    setTexto(valor);
  }, [respostas, questao?.tipo]);

  useEffect(() => {
    setDraftValor(null);
    setDraftOpcoesMap(null);
    setDraftDirty(false);
  }, [respostas, questao?.IDQuestao_Secao]);

  const salvarResposta = useCallback(
    async ({ descricao, IDQuestao_Opcao = null, respostaId = null }) => {
      if (readOnly) return;
      if (!questao?.IDQuestao_Secao || !IDOperador) return;

      const payload = {
        IDQuestao_Secao: questao.IDQuestao_Secao,
        descricao,
        IDQuestao_Opcao,
        IDOperador,
      };

      try {
        setSaving(true);
        if (respostaId) {
          await api.put(`/chkquestaoresposta/${respostaId}`, payload);
        } else {
          await api.post('/chkquestaoresposta', payload);
        }
        await carregarRespostas({ silent: true });
      } catch (error) {
        console.error('Erro ao salvar resposta', error);
      } finally {
        setSaving(false);
      }
    },
    [questao, IDOperador, carregarRespostas, readOnly],
  );

  const excluirResposta = useCallback(
    async (respostaId) => {
      if (readOnly) return;
      if (!respostaId) return;
      try {
        setSaving(true);
        await api.delete(`/chkquestaoresposta/${respostaId}`);
        await carregarRespostas({ silent: true });
      } catch (error) {
        console.error('Erro ao excluir resposta', error);
      } finally {
        setSaving(false);
      }
    },
    [carregarRespostas, readOnly],
  );

  const respostaAtual =
    respostas.length > 1
      ? respostas?.find((opcao) => isOpcaoMarcada(opcao))
      : respostas?.[0];
  const valorAtual = respostaAtual?.resposta || respostaAtual?.descricao || '';
  const valorSelecionado = draftValor ?? valorAtual;
  const respostaAtualId = getRespostaId(respostaAtual);

  const opcoes = useMemo(() => {
    if (!Array.isArray(respostas)) return [];
    return respostas.map((opcao, index) => ({
      IDQuestao_Opcao: getOpcaoId(opcao) ?? index + 1,
      descricaoOpcao: getOpcaoDescricao(opcao) || `Opcao ${index + 1}`,
      opcaoMarcada: isOpcaoMarcada(opcao),
      dataUltimaResposta: opcao?.dataUltimaResposta || null,
      respostaId: getRespostaId(opcao),
    }));
  }, [respostas]);

  const opcoesComDraft = useMemo(() => {
    if (!draftOpcoesMap) return opcoes;
    return opcoes.map((opcao) => ({
      ...opcao,
      opcaoMarcada: Boolean(draftOpcoesMap[opcao.IDQuestao_Opcao]),
    }));
  }, [opcoes, draftOpcoesMap]);

  const respostaSelecionada = useMemo(
    () => opcoesComDraft.find((opcao) => opcao.opcaoMarcada),
    [opcoesComDraft],
  );
  const respostaSelecionadaServer = useMemo(
    () => opcoes.find((opcao) => opcao.opcaoMarcada),
    [opcoes],
  );
  const ultimaAtualizacao = useMemo(() => {
    if (!Array.isArray(respostas) || !respostas.length) return null;
    let melhor = null;
    respostas.forEach((item) => {
      const raw = item?.dataUltimaRespostaRaw;
      if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
        if (!melhor || raw > melhor.raw) {
          melhor = {
            raw,
            text: item?.dataUltimaResposta || '',
            operadorNome: item?.operadorNome || '',
          };
        }
      }
    });
    if (!melhor?.text) return null;
    return {
      data: melhor.text,
      operadorNome: melhor.operadorNome || '',
    };
  }, [respostas]);
  const caminhoExibicao = useMemo(() => {
    if (objeto?.tipo === 2) {
      return objeto?.descricao || objeto?.path || '';
    }
    const caminho = objeto?.caminho || '';
    const ultimoSeparador = caminho.lastIndexOf('/');
    if (ultimoSeparador === -1) return caminho;
    return caminho.slice(0, ultimoSeparador + 1);
  }, [objeto?.tipo, objeto?.descricao, objeto?.path, objeto?.caminho]);
  const respostaUnicaId =
    respostaSelecionada?.respostaId || respostaAtualId || null;
  const respostaVinculoId = useMemo(() => {
    const respostaMarcada = respostas.find(
      (item) => Boolean(getRespostaId(item)) && isOpcaoMarcada(item),
    );

    if (respostaMarcada) {
      return getRespostaId(respostaMarcada);
    }

    const primeiraRespostaComId = respostas.find((item) =>
      Boolean(getRespostaId(item)),
    );

    return getRespostaId(primeiraRespostaComId);
  }, [respostas]);
  const temArquivosSelecionados = arquivosSelecionados.length > 0;
  const exibirBoxAnexos = temArquivosSelecionados || anexos.length > 0;
  const respondeuServer = useMemo(() => {
    if (!questao?.tipo) return false;

    switch (questao.tipo) {
      case 1:
      case 2:
        return Boolean(valorAtual?.trim());
      case 3:
        return Boolean(valorAtual?.trim());
      case 4:
        return opcoes.some((opcao) => opcao.opcaoMarcada);
      case 5:
        return Boolean(respostaSelecionadaServer);
      default:
        return Boolean(valorAtual?.trim());
    }
  }, [questao?.tipo, valorAtual, opcoes, respostaSelecionadaServer]);
  const questaoInativa = questao?.situacao === 0;
  const podeResponder = !questaoInativa && !readOnly;

  useEffect(() => {
    if (!questao?.IDQuestao_Secao) return;
    onStatusChange?.(questao.IDQuestao_Secao, respondeuServer);
  }, [questao?.IDQuestao_Secao, respondeuServer, onStatusChange]);

  const avaliarOverflowDescricao = useCallback(() => {
    const elemento = descricaoRef.current;
    if (!elemento) return;
    const ultrapassouAltura = elemento.scrollHeight > elemento.clientHeight + 1;
    const ultrapassouLargura = elemento.scrollWidth > elemento.clientWidth + 1;
    const temTextoGrande =
      questao?.descricao?.length > descricaoMinimaParaToggle;
    setMostrarToggleDescricao(
      ultrapassouAltura || ultrapassouLargura || temTextoGrande,
    );
  }, [questao?.descricao]);

  useEffect(() => {
    if (descricaoExpandida) return;
    avaliarOverflowDescricao();
  }, [questao?.descricao, descricaoExpandida, avaliarOverflowDescricao]);

  useEffect(() => {
    const handleResize = () => {
      if (descricaoExpandida) return;
      avaliarOverflowDescricao();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [descricaoExpandida, avaliarOverflowDescricao]);


  const salvarTextoAgora = useCallback(
    async (valor) => {
      const textoLimpo = valor?.trim();
      if (!textoLimpo) {
        if (respostaAtualId) {
          await excluirResposta(respostaAtualId);
        }
        return;
      }

      await salvarResposta({
        descricao: textoLimpo,
        IDQuestao_Opcao: null,
        respostaId: respostaAtualId,
      });
    },
    [salvarResposta, excluirResposta, respostaAtualId],
  );

  const salvarDraftAgora = useCallback(async () => {
    if (!draftDirty || saving) return;

    if (questao?.tipo === 3) {
      await salvarTextoAgora(texto);
    }

    if (questao?.tipo === 1 || questao?.tipo === 2) {
      if (draftValor && draftValor.toUpperCase() !== valorAtual?.toUpperCase()) {
        await salvarResposta({
          descricao: draftValor,
          IDQuestao_Opcao: null,
          respostaId: respostaAtualId,
        });
      }
    }

    if (questao?.tipo === 5) {
      const selecionadaId = draftOpcoesMap
        ? Number(Object.keys(draftOpcoesMap).find((id) => draftOpcoesMap[id]))
        : respostaSelecionada?.IDQuestao_Opcao;

      if (
        selecionadaId &&
        Number(respostaSelecionadaServer?.IDQuestao_Opcao) !== Number(selecionadaId)
      ) {
        const opcao = opcoes.find((o) => Number(o.IDQuestao_Opcao) === Number(selecionadaId));
        if (opcao) {
          await salvarResposta({
            descricao: opcao.descricaoOpcao,
            IDQuestao_Opcao: opcao.IDQuestao_Opcao,
            respostaId: respostaUnicaId,
          });
        }
      }
    }

    if (questao?.tipo === 4 && draftOpcoesMap) {
      for (const opcao of opcoes) {
        const desejado = Boolean(draftOpcoesMap[opcao.IDQuestao_Opcao]);
        if (desejado && !opcao.opcaoMarcada) {
          await salvarResposta({
            descricao: opcao.descricaoOpcao,
            IDQuestao_Opcao: opcao.IDQuestao_Opcao,
            respostaId: opcao.respostaId,
          });
        }
        if (!desejado && opcao.opcaoMarcada && opcao.respostaId) {
          await excluirResposta(opcao.respostaId);
        }
      }
    }

    await carregarRespostas({ silent: true });
    setDraftValor(null);
    setDraftOpcoesMap(null);
    setDraftDirty(false);
  }, [
    draftDirty,
    questao?.tipo,
    texto,
    draftValor,
    valorAtual,
    respostaAtualId,
    draftOpcoesMap,
    respostaSelecionada,
    respostaSelecionadaServer,
    opcoes,
    respostaUnicaId,
    salvarResposta,
    excluirResposta,
    carregarRespostas,
    salvarTextoAgora,
    saving,
  ]);

  useEffect(() => {
    if (selecionadaRef.current && !isSelected) {
      salvarDraftAgora();
    }
    selecionadaRef.current = isSelected;
  }, [isSelected, salvarDraftAgora]);

  const handleTextoChange = (valor) => {
    if (!podeResponder) return;
    setTexto(valor);
    setDraftDirty(true);
  };

  const handleTextoBlur = () => {
    if (!podeResponder) return;
    salvarDraftAgora();
  };

  const handleSalvarDraftClick = async () => {
    if (!podeResponder) return;
    await salvarDraftAgora();
  };

  const handleRespostaSimNao = async (valor) => {
    if (!podeResponder) return;
    if (saving) return;
    if (!valor) return;
    if (valorAtual?.toUpperCase() === valor) return;
    setDraftValor(valor);
    setDraftDirty(false);
    await salvarResposta({
      descricao: valor,
      IDQuestao_Opcao: null,
      respostaId: respostaAtualId,
    });
  };

  const handleRespostaConforme = async (valor) => {
    if (!podeResponder) return;
    if (saving) return;
    if (!valor) return;
    if (valorAtual?.toUpperCase() === valor) return;
    setDraftValor(valor);
    setDraftDirty(false);
    await salvarResposta({
      descricao: valor,
      IDQuestao_Opcao: null,
      respostaId: respostaAtualId,
    });
  };

  const handleRespostaUnica = async (opcao) => {
    if (!podeResponder) return;
    const opcaoId = getOpcaoId(opcao);
    if (!opcaoId) return;
    if (respostaSelecionada?.IDQuestao_Opcao === opcaoId) return;

    setDraftOpcoesMap({ [opcaoId]: true });
    setDraftDirty(true);

    await salvarResposta({
      descricao: getOpcaoDescricao(opcao),
      IDQuestao_Opcao: opcaoId,
      respostaId: respostaUnicaId,
    });
  };

  const handleRespostaMultipla = async (opcao) => {
    if (!podeResponder) return;
    const opcaoId = getOpcaoId(opcao);
    if (!opcaoId) return;
    setDraftOpcoesMap((prev) => {
      const base = prev || opcoes.reduce((acc, item) => {
        acc[item.IDQuestao_Opcao] = item.opcaoMarcada;
        return acc;
      }, {});
      const next = { ...base, [opcaoId]: !base[opcaoId] };
      return next;
    });
    setDraftDirty(true);
  };

  const handleSelecionarArquivos = () => {
    if (!podeResponder || uploadingAnexos) return;
    fileInputRef.current?.click();
  };

  const handleArquivosChange = async (event) => {
    const arquivos = Array.from(event.target.files || []);

    if (!arquivos.length) {
      setArquivosSelecionados([]);
      return;
    }

    const arquivosGrandes = arquivos.filter((arquivo) => arquivo.size > MAX_FILE_SIZE);
    const arquivosInvalidos = arquivos.filter((arquivo) => {
      const extensao = `.${arquivo.name.split('.').pop()?.toLowerCase() || ''}`;
      return !(
        ACCEPTED_EXTENSIONS.has(extensao) ||
        ACCEPTED_MIME_TYPES.has(arquivo.type)
      );
    });

    let arquivosValidos = arquivos.filter(
      (arquivo) =>
        !arquivosGrandes.includes(arquivo) && !arquivosInvalidos.includes(arquivo),
    );

    if (arquivosValidos.length > MAX_FILES_PER_UPLOAD) {
      arquivosValidos = arquivosValidos.slice(0, MAX_FILES_PER_UPLOAD);
      await Swal.fire({
        icon: 'warning',
        title: 'Limite de arquivos',
        text: `Somente os primeiros ${MAX_FILES_PER_UPLOAD} arquivos foram mantidos para envio.`,
      });
    }

    if (arquivosGrandes.length) {
      await Swal.fire({
        icon: 'warning',
        title: 'Arquivos muito grandes',
        text: 'Arquivos acima de 800 MB foram ignorados.',
      });
    }

    if (arquivosInvalidos.length) {
      await Swal.fire({
        icon: 'warning',
        title: 'Formato não permitido',
        text: 'Somente PDF, imagens JPG/PNG e documentos Office/LibreOffice são aceitos.',
      });
    }

    setArquivosSelecionados(arquivosValidos);

    if (fileInputRef.current && !arquivosValidos.length) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirmarAnexos = async () => {
    if (!podeResponder || !arquivosSelecionados.length || uploadingAnexos) return;
    if (!questao?.IDQuestao_Secao || !IDOperador || !permissaoPrincipal) return;

    try {
      setUploadingAnexos(true);

      const diretorio = await buscarDiretorioAnexosQuestao(
        questao.IDQuestao_Secao,
      );

      if (!diretorio?.IDDiretorio || !diretorio?.nomeBucketAws) {
        throw new Error('Diretório de anexos da seção não localizado.');
      }

      const arquivosEnviados = await uploadArquivosQuestaoResposta({
        arquivos: arquivosSelecionados,
        IDDiretorio: diretorio.IDDiretorio,
        bucketName: diretorio.nomeBucketAws,
        tipoPermissao: permissaoPrincipal,
        IDCriador: IDOperador,
      });

      const idsArquivos = (arquivosEnviados || [])
        .map((arquivo) => Number(arquivo.IDArquivo))
        .filter((id) => Number.isInteger(id) && id > 0);

      if (!idsArquivos.length) {
        throw new Error('O upload foi concluído, mas nenhum arquivo retornou da API.');
      }

      await vincularArquivosQuestaoResposta({
        IDQuestao_Secao: questao.IDQuestao_Secao,
        IDQuestao_Resposta: respostaVinculoId ?? null,
        IDArquivos: idsArquivos,
        IDCriador: IDOperador,
      });

      await carregarAnexos({ silent: true });
      setArquivosSelecionados([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      await Swal.fire({
        icon: 'success',
        title: 'Anexos enviados',
        text: `${idsArquivos.length} arquivo(s) vinculado(s) à questão.`,
      });
    } catch (error) {
      console.error('Erro ao enviar anexos da questão', error);
      const mensagem =
        error?.response?.data?.errors?.default ||
        error?.response?.data?.error ||
        error?.message ||
        'Não foi possível anexar os arquivos a esta questão.';

      await Swal.fire({
        icon: 'error',
        title: 'Falha ao anexar arquivos',
        text: mensagem,
      });
    } finally {
      setUploadingAnexos(false);
    }
  };

  return (
    <div className="accQ-item open">
      <div className="accQ-header">
        <div className="d-flex flex-column">
          <div className="accQ-left">
            <strong className="accQ-title">
              <span className={`accQ-title-id ${QUESTAO_ID_BADGE_VARIANT_CLASS}`}>
                #{questao.IDQuestao}
              </span>{' '}
              <span className="accQ-title-text">{questao.titulo}</span>
            </strong>
          </div>
          <div
            className={`accQ-description-wrapper ${
              descricaoExpandida ? 'is-expanded' : 'is-collapsed'
            }`}
          >
            <span ref={descricaoRef} className="accQ-description">
              {questao.descricao}
            </span>
            {mostrarToggleDescricao && (
              <button
                type="button"
                className="accQ-description-toggle"
                onClick={() => setDescricaoExpandida((prev) => !prev)}
                aria-expanded={descricaoExpandida}
              >
                {descricaoExpandida ? 'Ver menos' : 'Ver mais'}
              </button>
            )}
          </div>
        </div>
        <div className="accQ-right">
          <div className="accQ-status-stack">
            {questaoInativa && (
              <span
                className="accQ-status-badge is-inativo"
                title="Questão inativa"
              >
                <FiXCircle className="accQ-status-icon" />
                Inativo
              </span>
            )}
            <span
              className={`accQ-status ${
                respondeuServer ? 'is-respondido' : 'is-pendente'
              }`}
              title={respondeuServer ? 'Questão respondida' : 'Questao pendente'}
            >
              {respondeuServer ? (
                <FiCheckSquare className="accQ-status-icon" />
              ) : (
                <FaRegClock className="accQ-status-icon" />
              )}
              {!respondeuServer && 'Pendente'}
            </span>
          </div>
        </div>
      </div>

      <div className="accQ-wrapper">
        <div className="accQ-objeto">
          <div className="accQ-objeto-info">
            <div className="accQ-objeto-processo">
              <div className="icon-processo">
                <FaArchive className="accQ-icon" />
              </div>
              <div className="accQ-label-objeto">
                {objeto ? objeto.processo : 'Nenhum arquivo vinculado.'}
              </div>
            </div>
            {objeto && (
              <div className="accQ-objeto-arquivo">
                <div className="icon-file">
                  <FaFileAlt className="accQ-icon" />
                </div>
                <div className="accQ-label-objeto accQ-label-objeto--path">
                  <span className="accQ-path" title={caminhoExibicao}>
                    {objeto?.tipo === 2 ? objeto?.descricao : objeto?.path}
                  </span>
                </div>
              </div>
            )}
          </div>
          {objeto && (
            <div className="accQ-objeto-actions">
              <button
                type="button"
                className="chk-btn-abrir"
                onClick={() => onAbrirArquivo?.(objeto, questao)}
              >
                <FaFolderOpen className="accQ-icon" />
                Abrir
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="accQ-wrapper">
        <div className="accQ-content">
          <div className="accQ-layout">
            <div className="chk-resposta-acao">
              {loading && (
                <p className="loading-text">Carregando resposta...</p>
              )}

              {!loading && questao.tipo === 1 && (
                <RespostaSimNao
                  respostas={respostas}
                  editable={podeResponder}
                  valorSelecionado={valorSelecionado}
                  onChange={handleRespostaSimNao}
                />
              )}

              {!loading && questao.tipo === 2 && (
                <RespostaConformeNaoConforme
                  respostas={respostas}
                  editable={podeResponder}
                  valorSelecionado={valorSelecionado}
                  onChange={handleRespostaConforme}
                />
              )}

              {!loading && questao.tipo === 3 && (
                <RespostaTexto
                  respostas={respostas}
                  editable={podeResponder}
                  valor={texto}
                  onChange={handleTextoChange}
                  onBlur={handleTextoBlur}
                  showSave={draftDirty}
                  onSave={handleSalvarDraftClick}
                  isSaving={saving}
                />
              )}

              {!loading && questao.tipo === 4 && (
                <RespostaMultiplaEscolha
                  opcoes={opcoesComDraft}
                  editable={podeResponder}
                  onToggle={handleRespostaMultipla}
                />
              )}

              {!loading && questao.tipo === 5 && (
                <RespostaUnicaEscolha
                  opcoes={opcoesComDraft}
                  editable={podeResponder}
                  onSelect={handleRespostaUnica}
                  grupo={`resposta-${questao.IDQuestao_Secao}`}
                />
              )}

              {!loading && (
                <div className="chk-resposta-info">
                  {ultimaAtualizacao?.data && (
                    <small>
                      {ultimaAtualizacao.operadorNome
                        ? `Auditor: ${ultimaAtualizacao.operadorNome} | `
                        : ''}
                      Última atualização: {ultimaAtualizacao.data}
                    </small>
                  )}
                  {saving && (
                    <small className="chk-resposta-salvando">Salvando...</small>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept=".pdf,.jpeg,.jpg,.png,.doc,.docx,.odt,.xls,.xlsx,.ods"
                onChange={handleArquivosChange}
              />

              {!exibirBoxAnexos && (
                <div className="accQ-anexo-actions">
                  <button
                    type="button"
                    className="chk-btn-anexar"
                    onClick={handleSelecionarArquivos}
                    disabled={!podeResponder || uploadingAnexos}
                  >
                    <FaPaperclip className="accQ-icon-inline" />
                    {uploadingAnexos ? 'Enviando...' : 'Anexar arquivos'}
                  </button>
                </div>
              )}

              {exibirBoxAnexos && (
                <div className="accQ-anexo-box">
                  <div className="accQ-anexo-header">
                    <div className="accQ-anexo-header-title">
                      <FaPaperclip aria-hidden="true" />
                      <strong>Anexos da resposta</strong>
                    </div>
                    <span className="accQ-anexo-count">
                      {anexos.length} {anexos.length === 1 ? 'arquivo' : 'arquivos'}
                    </span>
                  </div>

                  {temArquivosSelecionados && (
                    <div className="accQ-anexo-pending">
                      {arquivosSelecionados.length} arquivo(s) selecionado(s). Aperte para confirmar.
                    </div>
                  )}

                  <div className="accQ-anexo-actions">
                    {temArquivosSelecionados ? (
                      <button
                        type="button"
                        className="chk-btn-confirmar-anexo"
                        onClick={handleConfirmarAnexos}
                        disabled={uploadingAnexos}
                      >
                        {uploadingAnexos && (
                          <span
                            className="chk-btn-spinner"
                            aria-hidden="true"
                          />
                        )}
                        {uploadingAnexos ? 'Processando...' : 'Confirmar anexos'}
                      </button>
                    ) : (
                      <>
                        {!!anexos.length && (
                          <button
                            type="button"
                            className="chk-btn-exibir-anexos"
                            onClick={(event) => {
                              event.stopPropagation();
                              onExibirArquivos?.(questao, anexos);
                            }}
                          >
                            Exibir arquivos
                          </button>
                        )}
                        <button
                          type="button"
                          className="chk-btn-anexar"
                          onClick={handleSelecionarArquivos}
                          disabled={!podeResponder || uploadingAnexos}
                        >
                          <FaPaperclip className="accQ-icon-inline" />
                          {uploadingAnexos ? 'Enviando...' : 'Anexar arquivos'}
                        </button>
                      </>
                    )}
                  </div>

                  {loadingAnexos && (
                    <span className="accQ-anexo-status">Carregando anexos...</span>
                  )}

                  {!loadingAnexos && !anexos.length && (
                    <span className="accQ-anexo-status">Nenhum anexo enviado para esta questão.</span>
                  )}

                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestaoResponderCard;
